import axios from "axios"
import { getBearerToken } from "../lib/auth/desktopAuth"

const BASE_URL = import.meta.env.VITE_API_URL

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
})

// Desktop auth: attach the bearer token (no-op on web, where it returns null).
api.interceptors.request.use((config) => {
  const token = getBearerToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    const ct = (response.headers["content-type"] || "") as string
    if (!ct.includes("application/json") && !ct.includes("application/problem+json")) {
      const body = typeof response.data === "string" ? response.data.substring(0, 200) : "non-JSON body"
      throw new Error(
        `API returned ${ct} (expected JSON) for ${response.config.url}. ` + `Status: ${response.status}. Body: ${body}`
      )
    }
    return response
  },
  (error) => {
    if (error.response) {
      const ct = (error.response.headers?.["content-type"] || "") as string
      if (ct.includes("text/html")) {
        const body = typeof error.response.data === "string" ? error.response.data.substring(0, 200) : "HTML body"
        throw new Error(
          `API returned HTML (text/html) instead of JSON for ${error.response.config?.url}. ` +
            `Status: ${error.response.status}. Body: ${body}`
        )
      }
      if (error.response.status === 401 || error.response.status === 403) {
        console.error("Unauthorized access - redirecting to login")
        window.location.href = "/login"
      }
    }
    throw error
  }
)

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { method = "GET", body, headers } = options

  const response = await api.request<T>({
    url: path,
    method: method as string,
    data: body !== undefined ? JSON.parse(body as string) : undefined,
    headers: headers as Record<string, string>,
  })

  return response.data
}
